import React, { Component, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import styled from "styled-components";
import { Chip, CircularProgress } from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CampaignIcon from "@material-ui/icons/NotificationsActive";
import EventIcon from "@material-ui/icons/Event";
import InfoIcon from "@material-ui/icons/InfoOutlined";
import QuizIcon from "@material-ui/icons/HelpOutline";
import FeedIcon from "@material-ui/icons/DynamicFeed";
import { withTranslation } from "react-i18next";
import { communicationStateType } from "../../../types";
import CommunicationService from "../../../services/CommunicationService";
import {
  loadCommunications,
  loadCommunicationsCount,
  setCommunicationsPage
} from "../../../redux/actions";
import { theme as appTheme } from "../../../assets/theme/StyledTheme";

/**
 * Tipos (alinhados a `CommunicationList#getTypeLabel`):
 * 1 Notícia, 2 Evento, 3 Informe, 4 Teste, 5 Feed de Rotina.
 */
const TYPE_META = {
  1: { typeKey: "news", Icon: CampaignIcon, toneKey: "primary.main" },
  2: { typeKey: "event", Icon: EventIcon, tone: "#2e7d32" },
  3: { typeKey: "notice", Icon: InfoIcon, tone: "#1565c0" },
  4: { typeKey: "test", Icon: QuizIcon, tone: "#6a1b9a" },
  5: { typeKey: "routineFeed", Icon: FeedIcon, tone: "#ef6c00" }
};

const getTypeMeta = (type, t) => {
  const meta = TYPE_META[type];
  if (!meta) {
    return {
      label: t("communication.type.default"),
      Icon: InfoIcon,
      toneKey: "secondary.dark"
    };
  }
  return {
    ...meta,
    label: t(`communication.type.${meta.typeKey}`)
  };
};

const resolveTypeTone = meta => {
  if (meta && meta.tone) return meta.tone;
  if (meta && meta.toneKey === "primary.main") {
    return appTheme.color.primary.main;
  }
  return appTheme.color.secondary.dark;
};

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  /* Em modo compacto (widget do Home), o pai tem overflow: hidden e o scroll
   * precisa ser interno à própria lista. Em modo normal, o scroll é o da
   * página (infinite scroll via window). */
  ${p =>
    p.$compact
      ? `
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-right: 4px;
  `
      : ""}
`;

const Card = styled.button`
  display: grid;
  grid-template-columns: ${p =>
    p.$compact ? "140px 1fr 36px" : "220px 1fr 44px"};
  width: 100%;
  min-height: ${p => (p.$compact ? "104px" : "132px")};
  padding: 0;
  margin: 0;
  border: 1px solid rgba(0, 48, 59, 0.08);
  background: #ffffff;
  border-radius: 14px;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  text-align: left;
  font-family: inherit;
  position: relative;
  box-shadow: 0 1px 2px rgba(0, 48, 59, 0.06), 0 1px 3px rgba(0, 48, 59, 0.04);
  transition: transform 0.15s ease, box-shadow 0.2s ease,
    border-color 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(0, 48, 59, 0.16);
    box-shadow: 0 2px 4px rgba(0, 48, 59, 0.08),
      0 6px 16px rgba(0, 48, 59, 0.08);
  }

  &:focus-visible {
    border-color: ${({ theme }) => theme.color.primary.main};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.color.primary.light};
  }

  &[data-highlight="true"] {
    border-color: ${({ theme }) => theme.color.primary.main};
  }

  &[data-highlight="true"]::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    background: linear-gradient(
      180deg,
      ${({ theme }) => theme.color.primary.main},
      ${({ theme }) => theme.color.primary.dark}
    );
    z-index: 3;
  }
`;

const Cover = styled.div`
  position: relative;
  background-color: ${p => p.$tone || p.theme.color.primary.light};
  background-image: ${p => (p.$src ? `url(${p.$src})` : "none")};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;

  /* Prober oculto: detecta 404/erro de rede e sinaliza o fallback ao pai. */
  .cover-probe {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }

  /* Fade suave para a área de texto (oculta o recorte duro do background). */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0) 55%,
      rgba(255, 255, 255, 0.85) 88%,
      #ffffff 100%
    );
    pointer-events: none;
  }

  [data-highlight="true"] &::after {
    background: linear-gradient(
      to right,
      rgba(224, 242, 241, 0) 0%,
      rgba(224, 242, 241, 0) 55%,
      rgba(224, 242, 241, 0.85) 88%,
      #f1faf8 100%
    );
  }

  svg {
    position: relative;
    z-index: 1;
    color: rgba(255, 255, 255, 0.9);
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
    font-size: ${p => (p.$compact ? "36px" : "48px")};
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  min-width: 0;
  padding: ${p => (p.$compact ? "12px 10px 12px 4px" : "16px 12px 16px 4px")};
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;

  /* Remove qualquer sobra da gradient da Cover que pudesse ultrapassar. */
  background: transparent;

  [data-highlight="true"] & {
    background: linear-gradient(
      to right,
      #f1faf8 0%,
      #f1faf8 60%,
      #ffffff 100%
    );
  }
`;

const Title = styled.div`
  font-size: ${p => (p.$compact ? "15px" : "17px")};
  font-weight: 600;
  color: #00303b;
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
  color: #607681;
  font-size: ${p => (p.$compact ? "12px" : "13px")};
  flex-wrap: wrap;
`;

const TypeTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: ${p => p.$tone || p.theme.color.secondary.dark};
  svg {
    font-size: 16px;
  }
`;

const Chevron = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b2b2b2;
  svg {
    font-size: 28px;
  }
`;

const LoadContainer = styled.div`
  text-align: center;
  margin: 8px;
`;

/**
 * Card individual. Mantém estado local para detectar falha de carregamento
 * da imagem (404/bloqueio) e cai no fallback colorido com ícone.
 */
const CommunicationCard = ({
  communication,
  compact,
  coverSrc,
  meta,
  isUnread,
  needsAck,
  needsPresence,
  onClick,
  t
}) => {
  const [imgFailed, setImgFailed] = useState(false);
  const { Icon } = meta;
  const tone = resolveTypeTone(meta);
  const effectiveSrc = coverSrc && !imgFailed ? coverSrc : undefined;

  return (
    <Card
      type="button"
      $compact={compact}
      data-highlight={isUnread ? "true" : "false"}
      onClick={() => onClick(communication)}
      aria-label={communication.title}
    >
      <Cover $src={effectiveSrc} $tone={tone} $compact={compact}>
        {coverSrc && !imgFailed && (
          <img
            className="cover-probe"
            src={coverSrc}
            alt=""
            onError={() => setImgFailed(true)}
          />
        )}
        {!effectiveSrc && <Icon />}
      </Cover>

      <Content $compact={compact}>
        <Title $compact={compact}>{communication.title}</Title>

        {(isUnread || needsAck || needsPresence) && (
          <ChipsRow>
            {isUnread && (
              <Chip
                size="small"
                label={t("communication.badge.new")}
                color="primary"
                style={{ height: 22, fontSize: 12, fontWeight: 600 }}
              />
            )}
            {needsAck && (
              <Chip
                size="small"
                label={t("communication.badge.pendingAck")}
                color="secondary"
                variant="outlined"
                style={{ height: 22, fontSize: 12, fontWeight: 600 }}
              />
            )}
            {needsPresence && (
              <Chip
                size="small"
                label={t("communication.badge.confirmPresence")}
                color="primary"
                variant="outlined"
                style={{ height: 22, fontSize: 12, fontWeight: 600 }}
              />
            )}
          </ChipsRow>
        )}

        <MetaRow $compact={compact}>
          <TypeTag $tone={tone}>
            <Icon />
            {meta.label}
          </TypeTag>
          <span>{moment(communication.date).format("DD/MM/YYYY HH:mm")}</span>
        </MetaRow>
      </Content>

      <Chevron>
        <ChevronRightIcon />
      </Chevron>
    </Card>
  );
};

CommunicationCard.propTypes = {
  communication: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    title: PropTypes.string,
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
  }).isRequired,
  compact: PropTypes.bool.isRequired,
  coverSrc: PropTypes.string,
  meta: PropTypes.shape({
    label: PropTypes.string.isRequired,
    Icon: PropTypes.elementType.isRequired,
    tone: PropTypes.string,
    toneKey: PropTypes.string
  }).isRequired,
  isUnread: PropTypes.bool,
  needsAck: PropTypes.bool,
  needsPresence: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired
};

CommunicationCard.defaultProps = {
  coverSrc: undefined,
  isUnread: false,
  needsAck: false,
  needsPresence: false
};

class CommunicationList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      hasAllCommunications: false
    };
  }

  componentDidMount() {
    const { size } = this.props;
    this.getCommunications();
    if (size !== "compact") {
      window.addEventListener("scroll", this.scrollEvent);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.scrollEvent);
  }

  getCommunications = async () => {
    const {
      communicationState,
      loadCommunications: onLoadCommunications,
      loadCommunicationsCount: onLoadCommunicationsCount,
      setCommunicationsPage: setPage
    } = this.props;
    const { communications, page } = communicationState;

    if (communications.length === 0) {
      this.setState({ loading: true });
      try {
        const newCommunications = await CommunicationService.getCommunications(
          page
        );
        onLoadCommunications(newCommunications);
        setPage(page + 1);

        const res = await CommunicationService.getCommunicationsCount();
        onLoadCommunicationsCount(res.data);
      } finally {
        this.setState({ loading: false });
      }
    }
  };

  getMoreCommunications = async () => {
    const {
      communicationState,
      loadCommunications: onLoadCommunications,
      setCommunicationsPage: setPage
    } = this.props;
    const { page } = communicationState;
    let { communications } = communicationState;
    const { hasAllCommunications } = this.state;

    if (hasAllCommunications) return;

    this.setState({ loading: true });
    const newCommunications = await CommunicationService.getCommunications(
      page
    );
    communications = communications.concat(newCommunications);
    onLoadCommunications(communications);
    setPage(page + 1);
    this.setState({
      loading: false,
      hasAllCommunications: newCommunications.length === 0
    });
  };

  scrollEvent = () => {
    const { loading } = this.state;
    const container = document.querySelector("html");
    if (
      !loading &&
      container.scrollTop + window.innerHeight >= container.offsetHeight
    ) {
      this.getMoreCommunications();
    }
  };

  rowPhoto = communication => {
    const src = communication.cover || communication.photo;
    return src && String(src).trim() ? src : undefined;
  };

  renderCard = communication => {
    const { onClick, size, t } = this.props;
    const compact = size === "compact";
    const coverSrc = this.rowPhoto(communication);
    const meta = getTypeMeta(communication.type, t);

    const isUnread =
      !!communication.interaction && !communication.interaction.openedAt;
    const needsAck =
      !!communication.interaction && communication.interaction.hasAck === false;
    const needsPresence =
      communication.type === 2 &&
      !!communication.interaction &&
      !communication.interaction.confirmedPresenceAt;

    return (
      <CommunicationCard
        key={communication.id}
        communication={communication}
        compact={compact}
        coverSrc={coverSrc}
        meta={meta}
        isUnread={isUnread}
        needsAck={needsAck}
        needsPresence={needsPresence}
        onClick={onClick}
        t={t}
      />
    );
  };

  render() {
    const { className, communicationState, size } = this.props;
    const { communications } = communicationState;
    const { loading } = this.state;
    const compact = size === "compact";

    return (
      <ListWrapper className={className} $compact={compact}>
        {communications.map(this.renderCard)}
        {loading && (
          <LoadContainer>
            <CircularProgress />
          </LoadContainer>
        )}
      </ListWrapper>
    );
  }
}

CommunicationList.propTypes = {
  size: PropTypes.oneOf(["compact", "normal"]),
  onClick: PropTypes.func.isRequired,
  className: PropTypes.string,
  communicationState: communicationStateType.isRequired, // injected by react-redux mapStateToProps connect()
  loadCommunications: PropTypes.func.isRequired, // injected by react-redux mapDispatchToProps connect()
  loadCommunicationsCount: PropTypes.func.isRequired, // injected by react-redux mapDispatchToProps connect()
  setCommunicationsPage: PropTypes.func.isRequired, // injected by react-redux mapDispatchToProps connect()
  t: PropTypes.func.isRequired
};

CommunicationList.defaultProps = {
  size: "normal",
  className: ""
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    { loadCommunications, loadCommunicationsCount, setCommunicationsPage },
    dispatch
  );

const mapStateToProps = state => ({
  communicationState: state.communicationState
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(CommunicationList));
